import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectField } from "@/lib/crm";
import { ASSET_STATUS, AssetStatus, Empty, options, useCategories } from "@/lib/assets";
import { fmtDate } from "@/lib/format";
import { DemoBadge, Panel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AssetDialog } from "@/components/assets/AssetDialog";

const warrantyTone = (w) => { if (!w) return ""; const d = (new Date(w) - new Date()) / 864e5; return d < 0 ? "text-red-600" : d <= 60 ? "text-amber-700" : "text-slate-500"; };

export const AssetsList = ({ company }) => {
  const { can } = useAuth();
  const cats = useCategories();
  const [flt, setFlt] = useState({ q: "", status: "", category: "", warranty: "" });
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries({ ...flt, company_id: company?.id }).filter(([, v]) => v));
    api.get("/assets", { params: { ...params, limit: 100 } }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
  }, [flt, company?.id]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const v = (k) => (val) => setFlt((x) => ({ ...x, [k]: val }));
  return (
    <Panel className="p-4 md:p-5" data-testid="assets-list">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="relative min-w-[200px] flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Cerca nome, codice, seriale, marca" value={flt.q} onChange={(e) => setFlt((x) => ({ ...x, q: e.target.value }))} data-testid="assets-search" /></div>
        <SelectField value={flt.status} onChange={v("status")} options={options(ASSET_STATUS)} allowNone="Tutti gli stati" placeholder="Stato" testId="assets-filter-status" className="w-40" />
        <SelectField value={flt.category} onChange={v("category")} options={cats} allowNone="Tutte le categorie" placeholder="Categoria" testId="assets-filter-category" className="w-44" />
        <SelectField value={flt.warranty} onChange={v("warranty")} options={[{ key: "in_scadenza", label: "Garanzia in scadenza" }, { key: "scaduta", label: "Garanzia scaduta" }]} allowNone="Garanzia: tutte" placeholder="Garanzia" testId="assets-filter-warranty" className="w-44" />
        {can("assets:create") && !company?.archived_at && <Button className="gap-1" onClick={() => setOpen(true)} data-testid="asset-new"><Plus className="h-4 w-4" />Nuovo asset</Button>}
      </div>
      {data && !data.items.length && <Empty testId="assets-empty">Nessun asset registrato.</Empty>}
      <ul className="divide-y divide-line">{(data?.items || []).map((a) => (
        <li key={a.id} data-testid={`asset-row-${a.id}`}>
          <Link to={`/prodotti-asset/${a.id}`} className="flex flex-wrap items-center gap-3 py-3 hover:bg-slate-50/60" data-testid={`asset-link-${a.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-xs font-semibold text-slate-400">{a.code}{a.serial_number ? ` · S/N ${a.serial_number}` : ""}</p>
              <p className="truncate text-sm font-semibold text-ink">{a.name} {a.is_demo && <DemoBadge className="ml-1" />}</p>
              <p className="text-xs text-slate-500">{[!company && a.company_name, a.category_label, a.site_name, [a.brand, a.model].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}</p>
            </div>
            {a.warranty_until && <span className={`text-xs ${warrantyTone(a.warranty_until)}`} data-testid={`asset-warranty-${a.id}`}>Garanzia {fmtDate(a.warranty_until)}</span>}
            <AssetStatus value={a.status} testId={`asset-status-${a.id}`} />
          </Link>
        </li>))}</ul>
      {data && data.total > data.items.length && <p className="mt-3 text-xs text-slate-500">Mostrati {data.items.length} di {data.total}: affina i filtri.</p>}
      <AssetDialog open={open} onOpenChange={setOpen} presetCompany={company ? { id: company.id, name: company.name } : null} onSaved={load} />
    </Panel>
  );
};
